import { z } from 'zod';
import { Entity } from '../../index.js';
import { PerformedBySchema } from '../../performed-by/index.js';
import { InteractionProvider, ReturnReason } from '../index.js';
import { EBoksDeliveredEventDataSchema, EBoksFailedEventDataSchema, } from './e-boks.js';
import { LinkMobilityEventSchema } from './link-mobility.js';
import { MailgunClickedEventSchema, MailgunDeliveredEventSchema, MailgunFailedEventSchema, MailgunOpenedEventSchema, } from './mailgun.js';
import { SparkPostBounceEventSchema, SparkPostClickedEventSchema, SparkPostComplainedEventSchema, SparkPostDelayEventSchema, SparkPostDeliveredEventSchema, SparkPostInitialOpenEventSchema, SparkPostInternalErrorEventSchema, SparkPostOpenEventSchema, SparkPostOutOfBandEventSchema, } from './spark-post.js';
import { SproomCreatedEventSchema, SproomEndpointNotFoundEventSchema, SproomFailedEventSchema, SproomReceivedEventSchema, SproomSendNemHandelEventSchema, SproomSentEventSchema, SproomTransmissionCompletedEventSchema, SproomTransmissionStartedEventSchema, } from './sproom.js';
import { StraalforsFailedEventSchema } from './straalfors.js';
import { VonageCallAnalysisEventBronzeSchema, VonageCallCompletedEventBronzeSchema, VonageCallErrorEventBronzeSchema, VonageCallProgressEventBronzeSchema, VonageInboundEventBronzeSchema, VonageInputEventBronzeSchema, VonageRecordingEventBronzeSchema, } from './vonage-call.js';
import { VonageSMSEventSchema } from './vonage-sms.js';
export var InteractionEventType;
(function (InteractionEventType) {
    InteractionEventType["Journaled"] = "journaled";
    InteractionEventType["Copied"] = "copied";
    InteractionEventType["SendingApproved"] = "sending_approved";
    InteractionEventType["Delivered"] = "delivered";
    InteractionEventType["Opened"] = "opened";
    InteractionEventType["Clicked"] = "clicked";
    InteractionEventType["Complained"] = "complained";
    InteractionEventType["Failed"] = "failed";
    InteractionEventType["Delayed"] = "delayed";
    // Voice-specific events
    InteractionEventType["Inbound"] = "inbound";
    InteractionEventType["Answered"] = "answered";
    InteractionEventType["Cancelled"] = "cancelled";
    InteractionEventType["Started"] = "started";
    InteractionEventType["Ringing"] = "ringing";
    InteractionEventType["Voicemail"] = "voicemail";
    InteractionEventType["HumanAnswered"] = "human_answered";
    InteractionEventType["DigitsEntered"] = "digits_entered";
    InteractionEventType["Completed"] = "completed";
    // E-invoicing-specific events
    InteractionEventType["Created"] = "created";
    InteractionEventType["TransmissionStarted"] = "transmissionStarted";
    InteractionEventType["Sent"] = "sent";
    InteractionEventType["Received"] = "received";
    // Letter-specific events
    InteractionEventType["TransferredToPrintAndDistribution"] = "transferred_to_print_and_distribution";
})(InteractionEventType || (InteractionEventType = {}));
const BaseEventSchema = z.object({
    id: z.string(),
    createdAt: z.string(),
    performedBy: PerformedBySchema.nullable(),
});
// Common events
const NewJournaledEventSchema = z.object({
    type: z.literal(InteractionEventType.Journaled),
    data: z.object({
        referenceId: z.string().nullable(),
        referenceType: z.enum(Entity).nullable(),
    }),
    provider: z.literal(InteractionProvider.System),
    bronzeData: z.null(),
});
const JournaledEventSchema = NewJournaledEventSchema.and(BaseEventSchema);
const NewCopiedEventSchema = z.object({
    type: z.literal(InteractionEventType.Copied),
    data: z.object({
        from: z
            .object({
            interactionId: z.string(),
            referenceId: z.string(),
        })
            .nullable(),
        to: z
            .object({
            interactionId: z.string(),
            referenceId: z.string(),
        })
            .nullable(),
        referenceType: z.enum(Entity),
    }),
    provider: z.literal(InteractionProvider.System),
    bronzeData: z.null(),
});
const CopiedEventSchema = NewCopiedEventSchema.and(BaseEventSchema);
const NewSendingApprovedEventSchema = z.object({
    type: z.literal(InteractionEventType.SendingApproved),
    data: z.object({}),
    provider: z.literal(InteractionProvider.System),
    bronzeData: z.null(),
});
const SendingApprovedEventSchema = NewSendingApprovedEventSchema.and(BaseEventSchema);
// Letter-specific events
export const NewTransferredToPrintAndDistributionEventSchema = z.object({
    type: z.literal(InteractionEventType.TransferredToPrintAndDistribution),
    data: z.object({}),
    provider: z.literal(InteractionProvider.Straalfors),
    bronzeData: z.null(),
});
export const TransferredToPrintAndDistributionEventSchema = NewTransferredToPrintAndDistributionEventSchema.and(BaseEventSchema);
const NewClickedEventSchema = z.object({
    type: z.literal(InteractionEventType.Clicked),
    data: z.object({
        geoIp: z
            .object({
            zipcode: z.string().optional(),
            city: z.string().optional(),
            country: z.string().optional(),
            longitude: z.number().optional(),
            latitude: z.number().optional(),
        })
            .optional(),
        referer: z.string().optional(),
        userAgent: z.string().optional(),
        link: z.string().optional(),
        urlShortenerId: z.string().optional(),
        ip: z.string().optional(),
    }),
    provider: z.literal(InteractionProvider.System),
    bronzeData: z.null(),
});
const ClickedEventSchema = NewClickedEventSchema.and(BaseEventSchema);
// Email events
const NewEmailFailedEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Failed),
    data: z.object({
        recipientEmail: z.string().optional(),
        bounceClass: z.string().optional(),
        errorCode: z.string().optional(),
        reason: z.string().optional(),
    }),
})
    .and(z.union([
    SparkPostBounceEventSchema,
    SparkPostOutOfBandEventSchema,
    SparkPostInternalErrorEventSchema,
    MailgunFailedEventSchema,
    z.object({
        provider: z.union([
            z.literal(InteractionProvider.SparkPost),
            z.literal(InteractionProvider.Mailgun),
        ]),
        bronzeData: z.string(),
    }),
]));
const EmailFailedEventSchema = NewEmailFailedEventSchema.and(BaseEventSchema);
const NewEmailDeliveredEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Delivered),
    data: z.object({
        recipientDomain: z.string().optional(),
        recipientEmail: z.string().optional(),
        mailboxProvider: z.string().optional(),
        recipientIpAddress: z.string().optional(),
        deliveryMethod: z.string().optional(),
        routingDomain: z.string().optional(),
    }),
})
    .and(z.union([SparkPostDeliveredEventSchema, MailgunDeliveredEventSchema]));
const EmailDeliveredEventSchema = NewEmailDeliveredEventSchema.and(BaseEventSchema);
const NewEmailOpenedEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Opened),
    data: z.object({
        geoIp: z
            .object({
            zipcode: z.string().optional(),
            city: z.string().optional(),
            country: z.string().optional(),
            longitude: z.number().optional(),
            latitude: z.number().optional(),
        })
            .optional(),
    }),
})
    .and(z.union([
    SparkPostOpenEventSchema,
    SparkPostInitialOpenEventSchema,
    MailgunOpenedEventSchema,
]));
const EmailOpenedEventSchema = NewEmailOpenedEventSchema.and(BaseEventSchema);
const NewEmailClickedEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Clicked),
    data: z.object({
        geoIp: z
            .object({
            zipcode: z.string().optional(),
            city: z.string().optional(),
            country: z.string().optional(),
            longitude: z.number().optional(),
            latitude: z.number().optional(),
        })
            .optional(),
        referer: z.string().optional(),
        userAgent: z.string().optional(),
        link: z.string().optional(),
        urlShortenerId: z.string().optional(),
        ip: z.string().optional(),
    }),
})
    .and(z.union([SparkPostClickedEventSchema, MailgunClickedEventSchema]));
const EmailClickedEventSchema = NewEmailClickedEventSchema.and(BaseEventSchema);
const NewEmailDelayedEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Delayed),
    data: z.object({
        reason: z.string().optional(),
        recipientEmail: z.string().optional(),
        bounceClass: z.string().optional(),
        errorCode: z.string().optional(),
        queueTime: z.string().optional(),
        numRetries: z.string().optional(),
    }),
})
    .and(z.union([SparkPostDelayEventSchema, MailgunFailedEventSchema]));
const EmailDelayedEventSchema = NewEmailDelayedEventSchema.and(BaseEventSchema);
const NewEmailComplainedEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Complained),
    data: z.object({}),
})
    .and(z.union([SparkPostComplainedEventSchema]));
const EmailComplainedEventSchema = NewEmailComplainedEventSchema.and(BaseEventSchema);
export const NewEmailEventSchema = z.union([
    NewEmailFailedEventSchema,
    NewEmailDeliveredEventSchema,
    NewEmailOpenedEventSchema,
    NewEmailClickedEventSchema,
    NewEmailDelayedEventSchema,
    NewEmailComplainedEventSchema,
    NewJournaledEventSchema,
    NewCopiedEventSchema,
    NewClickedEventSchema,
    NewSendingApprovedEventSchema,
]);
export const EmailEventSchema = z.union([
    EmailFailedEventSchema,
    EmailDeliveredEventSchema,
    EmailOpenedEventSchema,
    EmailClickedEventSchema,
    EmailDelayedEventSchema,
    EmailComplainedEventSchema,
    JournaledEventSchema,
    CopiedEventSchema,
    ClickedEventSchema,
    SendingApprovedEventSchema,
]);
// SMS events
const NewSMSDeliveredEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Delivered),
    data: z.object({
        operator: z.string().optional(),
    }),
})
    .and(z.union([LinkMobilityEventSchema, VonageSMSEventSchema]));
const SMSDeliveredEventSchema = NewSMSDeliveredEventSchema.and(BaseEventSchema);
const NewSMSFailedEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Failed),
    data: z.object({
        reason: z.string().optional(),
    }),
})
    .and(z.union([LinkMobilityEventSchema, VonageSMSEventSchema]));
const SMSFailedEventSchema = NewSMSFailedEventSchema.and(BaseEventSchema);
export const NewSmsEventSchema = z.union([
    NewSMSDeliveredEventSchema,
    NewSMSFailedEventSchema,
    NewJournaledEventSchema,
    NewCopiedEventSchema,
    NewClickedEventSchema,
    NewSendingApprovedEventSchema,
]);
export const SmsEventSchema = z.union([
    SMSDeliveredEventSchema,
    SMSFailedEventSchema,
    JournaledEventSchema,
    CopiedEventSchema,
    ClickedEventSchema,
    SendingApprovedEventSchema,
]);
// e-Boks events
const NewEBoksDeliveredEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Delivered),
    data: z.object({
        messageId: z.string().optional(),
    }),
})
    .and(EBoksDeliveredEventDataSchema);
const EBoksDeliveredEventSchema = NewEBoksDeliveredEventSchema.and(BaseEventSchema);
const NewEBoksFailedEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Failed),
    data: z.object({
        errorId: z.string().optional(),
        errorCode: z.number().optional(),
        errorText: z.string().optional(),
    }),
})
    .and(EBoksFailedEventDataSchema);
const EBoksFailedEventSchema = NewEBoksFailedEventSchema.and(BaseEventSchema);
export const NewEBoksEventSchema = z.union([
    NewEBoksDeliveredEventSchema,
    NewEBoksFailedEventSchema,
    NewJournaledEventSchema,
    NewCopiedEventSchema,
    NewClickedEventSchema,
    NewSendingApprovedEventSchema,
]);
export const EBoksEventSchema = z.union([
    EBoksDeliveredEventSchema,
    EBoksFailedEventSchema,
    JournaledEventSchema,
    CopiedEventSchema,
    ClickedEventSchema,
    SendingApprovedEventSchema,
]);
// Letter events
export const NewLetterFailedEventSchema = StraalforsFailedEventSchema.extend({
    type: z.literal(InteractionEventType.Failed),
    data: z.object({
        returnReason: z.enum(ReturnReason).optional(),
    }),
});
export const LetterFailedEventSchema = NewLetterFailedEventSchema.and(BaseEventSchema);
export const NewLetterEventSchema = z.union([
    NewLetterFailedEventSchema,
    NewTransferredToPrintAndDistributionEventSchema,
    NewJournaledEventSchema,
    NewCopiedEventSchema,
    NewClickedEventSchema,
    NewSendingApprovedEventSchema,
]);
export const LetterEventSchema = z.union([
    LetterFailedEventSchema,
    TransferredToPrintAndDistributionEventSchema,
    JournaledEventSchema,
    CopiedEventSchema,
    ClickedEventSchema,
    SendingApprovedEventSchema,
]);
// E-invoice events
export const NewEInvoiceSentEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Sent),
    data: z.object({}),
})
    .and(SproomSentEventSchema);
export const EInvoiceSentEventSchema = NewEInvoiceSentEventSchema.and(BaseEventSchema);
export const NewEInvoiceDeliveredEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Delivered),
    data: z.object({}),
})
    .and(SproomTransmissionCompletedEventSchema);
export const EInvoiceDeliveredEventSchema = NewEInvoiceDeliveredEventSchema.and(BaseEventSchema);
export const NewEInvoiceCreatedEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Created),
    data: z.object({}),
})
    .and(SproomCreatedEventSchema);
export const EInvoiceCreatedEventSchema = NewEInvoiceCreatedEventSchema.and(BaseEventSchema);
export const NewEInvoiceFailedEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Failed),
    data: z.object({}),
})
    .and(z.union([
    SproomSendNemHandelEventSchema,
    SproomFailedEventSchema,
    SproomEndpointNotFoundEventSchema,
]));
export const EInvoiceFailedEventSchema = NewEInvoiceFailedEventSchema.and(BaseEventSchema);
export const NewEInvoiceTransmissionStartedEventSchema = z
    .object({
    type: z.literal(InteractionEventType.TransmissionStarted),
    data: z.object({}),
})
    .and(SproomTransmissionStartedEventSchema);
export const EInvoiceTransmissionStartedEventSchema = NewEInvoiceTransmissionStartedEventSchema.and(BaseEventSchema);
export const NewEInvoiceReceivedEventSchema = z
    .object({
    type: z.literal(InteractionEventType.Received),
    data: z.object({}),
})
    .and(SproomReceivedEventSchema);
export const EInvoiceReceivedEventSchema = NewEInvoiceReceivedEventSchema.and(BaseEventSchema);
export const NewEInvoiceEventSchema = z.union([
    NewEInvoiceSentEventSchema,
    NewEInvoiceDeliveredEventSchema,
    NewEInvoiceCreatedEventSchema,
    NewEInvoiceFailedEventSchema,
    NewEInvoiceTransmissionStartedEventSchema,
    NewEInvoiceReceivedEventSchema,
    NewJournaledEventSchema,
    NewCopiedEventSchema,
    NewClickedEventSchema,
]);
export const EInvoiceEventSchema = z.union([
    EInvoiceSentEventSchema,
    EInvoiceDeliveredEventSchema,
    EInvoiceCreatedEventSchema,
    EInvoiceFailedEventSchema,
    EInvoiceTransmissionStartedEventSchema,
    EInvoiceReceivedEventSchema,
    JournaledEventSchema,
    CopiedEventSchema,
    ClickedEventSchema,
]);
// Auto-call events
const NewAutoCallStartedEventSchema = z.object({
    type: z.literal(InteractionEventType.Started),
    data: z.object({}),
    provider: z.literal(InteractionProvider.VonageCall),
    bronzeData: z.union([VonageCallProgressEventBronzeSchema]),
});
const AutoCallStartedEventSchema = NewAutoCallStartedEventSchema.and(BaseEventSchema);
const NewAutoCallInboundEventSchema = z.object({
    type: z.literal(InteractionEventType.Inbound),
    data: z.object({}),
    provider: z.literal(InteractionProvider.VonageCall),
    bronzeData: VonageInboundEventBronzeSchema,
});
const AutoCallInboundEventSchema = NewAutoCallInboundEventSchema.and(BaseEventSchema);
const NewAutoCallAnsweredEventSchema = z.object({
    type: z.literal(InteractionEventType.Answered),
    data: z.object({}),
    provider: z.literal(InteractionProvider.VonageCall),
    bronzeData: VonageCallProgressEventBronzeSchema,
});
const AutoCallAnsweredEventSchema = NewAutoCallAnsweredEventSchema.and(BaseEventSchema);
const NewAutoCallDigitsEnteredEventSchema = z.object({
    type: z.literal(InteractionEventType.DigitsEntered),
    data: z.object({
        digits: z.string().optional(),
        timedOut: z.boolean().optional(),
    }),
    provider: z.literal(InteractionProvider.VonageCall),
    bronzeData: VonageInputEventBronzeSchema,
});
const AutoCallDigitsEnteredEventSchema = NewAutoCallDigitsEnteredEventSchema.and(BaseEventSchema);
const NewAutoCallCompletedEventSchema = z.object({
    type: z.literal(InteractionEventType.Completed),
    data: z.object({
        duration: z.string(),
        network: z.string().nullable().optional(),
    }),
    provider: z.literal(InteractionProvider.VonageCall),
    bronzeData: z.union([
        VonageCallCompletedEventBronzeSchema,
        VonageRecordingEventBronzeSchema,
    ]),
});
const AutoCallCompletedEventSchema = NewAutoCallCompletedEventSchema.and(BaseEventSchema);
const NewAutoCallRingingEventSchema = z.object({
    type: z.literal(InteractionEventType.Ringing),
    data: z.object({}),
    provider: z.literal(InteractionProvider.VonageCall),
    bronzeData: z.union([VonageCallProgressEventBronzeSchema]),
});
const AutoCallRingingEventSchema = NewAutoCallRingingEventSchema.and(BaseEventSchema);
const NewAutoCallCancelledEventSchema = z.object({
    type: z.literal(InteractionEventType.Cancelled),
    data: z.object({
        reason: z.string().optional(),
        errorCode: z.string().optional(),
    }),
    provider: z.literal(InteractionProvider.VonageCall),
    bronzeData: z.union([VonageCallErrorEventBronzeSchema]),
});
const AutoCallCancelledEventSchema = NewAutoCallCancelledEventSchema.and(BaseEventSchema);
const NewAutoCallHumanAnsweredEventSchema = z.object({
    type: z.literal(InteractionEventType.HumanAnswered),
    data: z.object({
        subState: z.string().optional(),
    }),
    provider: z.literal(InteractionProvider.VonageCall),
    bronzeData: z.union([VonageCallAnalysisEventBronzeSchema]),
});
const AutoCallHumanAnsweredEventSchema = NewAutoCallHumanAnsweredEventSchema.and(BaseEventSchema);
const NewAutoCallVoicemailEventSchema = z.object({
    type: z.literal(InteractionEventType.Voicemail),
    data: z.object({
        subState: z.string().optional(),
    }),
    provider: z.literal(InteractionProvider.VonageCall),
    bronzeData: z.union([VonageCallAnalysisEventBronzeSchema]),
});
const AutoCallVoicemailEventSchema = NewAutoCallVoicemailEventSchema.and(BaseEventSchema);
const NewAutoCallFailedEventSchema = z.object({
    type: z.literal(InteractionEventType.Failed),
    data: z.object({
        reason: z.string().optional(),
        errorCode: z.string().optional(),
    }),
    provider: z.literal(InteractionProvider.VonageCall),
    bronzeData: z.union([VonageCallErrorEventBronzeSchema]),
});
const AutoCallFailedEventSchema = NewAutoCallFailedEventSchema.and(BaseEventSchema);
export const NewAutoCallEventSchema = z.union([
    NewAutoCallInboundEventSchema,
    NewAutoCallStartedEventSchema,
    NewAutoCallRingingEventSchema,
    NewAutoCallAnsweredEventSchema,
    NewAutoCallDigitsEnteredEventSchema,
    NewAutoCallCompletedEventSchema,
    NewAutoCallCancelledEventSchema,
    NewAutoCallFailedEventSchema,
    NewAutoCallHumanAnsweredEventSchema,
    NewAutoCallVoicemailEventSchema,
    NewJournaledEventSchema,
    NewCopiedEventSchema,
    NewClickedEventSchema,
    NewSendingApprovedEventSchema,
]);
export const AutoCallEventSchema = z.union([
    AutoCallInboundEventSchema,
    AutoCallStartedEventSchema,
    AutoCallRingingEventSchema,
    AutoCallAnsweredEventSchema,
    AutoCallDigitsEnteredEventSchema,
    AutoCallCompletedEventSchema,
    AutoCallCancelledEventSchema,
    AutoCallFailedEventSchema,
    AutoCallHumanAnsweredEventSchema,
    AutoCallVoicemailEventSchema,
    JournaledEventSchema,
    CopiedEventSchema,
    ClickedEventSchema,
    SendingApprovedEventSchema,
]);
// Events every interaction type can receive regardless of channel: they are
// added by generic flows (journalization, approval, link tracking) that do not
// switch on the interaction type.
export const SystemInteractionEventSchema = z.union([
    JournaledEventSchema,
    CopiedEventSchema,
    ClickedEventSchema,
    SendingApprovedEventSchema,
]);
export const InteractionEventSchema = z.union([
    EmailEventSchema,
    SmsEventSchema,
    EBoksEventSchema,
    LetterEventSchema,
    EInvoiceEventSchema,
    AutoCallEventSchema,
]);
export const NewInteractionEventSchema = z.union([
    NewEmailEventSchema,
    NewSmsEventSchema,
    NewEBoksEventSchema,
    NewLetterEventSchema,
    NewEInvoiceEventSchema,
    NewAutoCallEventSchema,
]);
//# sourceMappingURL=index.js.map